import React, { useEffect, useState } from 'react'

// Direct file import, not the `components` barrel — this module is itself reachable from
// that barrel and the edge would close a cycle.
import { StatusPill, type StatusPillOption } from 'components/display/StatusPill/StatusPill'

import { Stack, Tooltip } from '@mui/material'

import { FilterField } from './FilterField'

// A multi-select filter rendered as StatusChip-style toggle chips (leading icon +
// status color), for status-family fields (Scan Type, Tampered, Certificate status).
// Selection is WEIGHT-NEUTRAL: the chip keeps the same 1px border and padding in both
// states, so toggling never shifts layout — selected just fills with the status tint;
// unselected is a muted outline. Emits the same { field, operator, value } shape as
// MultiSelectFilter so the Filters drawer treats it identically.

/**
 * A filter option = the shared pill's option, plus the two things only a FILTER needs.
 *
 * The visual half (`color`, `textColor`, `backgroundColor`, `borderColor`, `icon`) is
 * `StatusPillOption` and is defined once, beside the component that renders it — so a
 * change to the pill's inputs cannot leave this type describing something else. `value` is
 * required here (a filter option must identify what it selects; a pill can be decorative)
 * and `tooltip` belongs to the field, which decides whether to pass it down.
 */
export type StatusChipOption = StatusPillOption & {
  value: string
  /** Optional hover tooltip describing what this option means (e.g. a capability). */
  tooltip?: string
}

/**
 * Build StatusChipFilter options straight from a shared STATUSES config
 * (`{ [value]: { label, color } }` — e.g. INVOICE_STATUSES, ORDER_STATUSES,
 * PAYMENT_STATUSES). Any status-family column should set `type: 'statusChip'`
 * and `options: statusFilterOptions(THE_STATUSES)` so its filter renders as the
 * SAME colored status pills the table's Status column shows — one rule for every
 * status filter. Pass an optional icon map keyed by status value.
 */
export const statusFilterOptions = (
  statuses: Record<
    string,
    { label: string; color: string; backgroundColor?: string; borderColor?: string }
  >,
  icons?: Record<string, React.ReactElement>,
): StatusChipOption[] =>
  Object.entries(statuses).map(([value, cfg]) => ({
    value,
    label: cfg.label,
    color: cfg.color,
    // Pass the authored fill/edge through. Projecting a status down to just its hue here was the
    // reason every status filter in the app disagreed with the table beside it.
    backgroundColor: cfg.backgroundColor,
    borderColor: cfg.borderColor,
    icon: icons?.[value],
  }))

interface StatusChipFilterProps {
  column?: any
  filter?: string[]
  onChange?: (f: { field: string; operator: string; value: string[] }) => void
}

export const StatusChipFilter = ({ column, filter = [], onChange = () => {} }: StatusChipFilterProps) => {
  const [selected, setSelected] = useState<string[]>(filter)
  useEffect(() => setSelected(filter), [filter])

  const options: StatusChipOption[] = column?.options ?? []
  // 'badge' renders bigger, rounded-rect capability badges (matching the product-page
  // TagCapabilityBadges); default 'pill' is the small status token used elsewhere.
  const isBadge = column?.variant === 'badge'
  /**
   * Optional per-option renderer — the option draws ITSELF, this field only supplies the
   * click target and the toggle state.
   *
   * Exists so a surface that already HAS a badge component can filter with that exact
   * component rather than a lookalike built out of the styling below (§36). The support
   * list's Type facet is the first caller: its cards render `SupportTypeBadge`, and without
   * this the drawer would re-draw the same badge from a second set of rules — which is the
   * drift this codebase has repeatedly paid for (see the `statusTone` note above, where a
   * filter and the table it filtered painted the same status two different colours).
   *
   * Opt-in and additive: no existing column passes it, so every current filter keeps the
   * built-in pill/badge rendering untouched.
   */
  const renderOption = column?.renderOption as
    | ((option: StatusChipOption, isSelected: boolean) => React.ReactNode)
    | undefined

  const emit = (next: string[]) =>
    onChange({ field: column?.field, operator: 'isAnyOf', value: next })

  const toggle = (value: string) => {
    const next = selected.includes(value)
      ? selected.filter((v) => v !== value)
      : [...selected, value]
    setSelected(next)
    emit(next)
  }

  const clear = () => {
    setSelected([])
    emit([])
  }

  return (
    <FilterField label={column?.headerName} count={selected.length} showClear={selected.length > 0} onClear={clear}>
      <Stack direction="row" flexWrap="wrap" sx={{ gap: 2 }}>
        {options.map((opt) => {
          const isOn = selected.includes(opt.value)

          // The option supplies its own rendering — wrap it in the click target and nothing
          // else, so this field adds no border/fill of its own on top of what it drew.
          if (renderOption) {
            return (
              <Tooltip key={opt.value} title={opt.tooltip ?? ''} placement="bottom" arrow>
                <Stack
                  onClick={() => toggle(opt.value)}
                  sx={{ cursor: 'pointer', width: 'fit-content' }}
                >
                  {renderOption(opt, isOn)}
                </Stack>
              </Tooltip>
            )
          }

          /**
           * The pill itself now lives in `components/display/StatusPill` (§36 — the chart
           * legend became its second consumer, so it was extracted rather than copied).
           * Every declaration moved verbatim; the drawer's rendered output was measured
           * before and after to confirm nothing shifted.
           *
           * The one behavioural CHANGE, and it is a gain in both places: the pill is now a
           * real `<button>` with `aria-pressed` and a `:focus-visible` ring. This was a
           * `div` with an `onClick` — unreachable by keyboard, with no on/off state exposed
           * to assistive tech. Hover a capability chip to learn what it does (an empty
           * tooltip renders none).
           */
          return (
            <StatusPill
              key={opt.value}
              option={opt}
              selected={isOn}
              variant={isBadge ? 'badge' : 'pill'}
              tooltip={opt.tooltip}
              onClick={() => toggle(opt.value)}
            />
          )
        })}
      </Stack>
    </FilterField>
  )
}
